import { useSyncExternalStore } from 'react';

/**
 * 訂閱瀏覽器的連線狀態。
 *
 * **`navigator.onLine` 只回答「有沒有連上網路介面」，不回答「請求送得到嗎」。**
 * 連上一個沒有對外出口的 Wi-Fi、或後端掛掉時，它一樣是 `true`。因此這支 hook 適合
 * 用來**提示**（離線橫幅、把送出鍵停用），不能拿來當「這次請求會成功」的保證——
 * 真正的失敗仍然要由請求自己回報。
 *
 * 反過來說 `false` 是可信的：作業系統確定沒有網路介面可用時才會這樣回報，
 * 這種情況下任何請求都必定失敗，先講比讓使用者按下去等逾時要好。
 *
 * 以 `useSyncExternalStore` 實作，理由同 {@link useMediaQuery}：連線狀態是外部
 * 可訂閱源，這樣 render 階段就讀得到當下的值，不會先渲染一次「線上」再更新。
 *
 * @example
 * const isOnline = useOnlineStatus();
 * if (!isOnline) return <OfflineNotice />;
 */
export function useOnlineStatus(): boolean {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener('online', onStoreChange);
  window.addEventListener('offline', onStoreChange);

  return () => {
    window.removeEventListener('online', onStoreChange);
    window.removeEventListener('offline', onStoreChange);
  };
}

function getSnapshot(): boolean {
  return navigator.onLine;
}

/*
 * 靜態產生時沒有 navigator，且此時無從得知使用者的連線狀態。
 *
 * 這裡刻意猜「線上」而非「離線」：猜錯的代價不對稱——猜線上而實際離線，只是提示
 * 晚一個 tick 出現（掛載後立刻修正）；猜離線而實際線上，則是每個人開啟 app 的
 * 第一幀都會閃過一張「沒有網路」的卡片。
 */
function getServerSnapshot(): boolean {
  return true;
}
